import { io } from 'socket.io-client';
import Cookies from 'js-cookie';

class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.locationWatchId = null;
    this.SOCKET_URL = process.env.REACT_APP_SOCKET_URL || process.env.REACT_APP_URL_LOCAL_HOST || 'http://localhost:5001';
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
  }

  // ==================== CONEXIÓN ====================

  /**
   * Conectar socket
   * @param {string} token - Token JWT (opcional, si no se pasa intenta leerlo de cookies)
   */
  connect(token = null) {
    if (this.socket?.connected) {
      console.log('⚠️ Socket ya está conectado');
      return this.socket;
    }

    // Intentar obtener token de cookies si no se pasó como parámetro
      const newToken = localStorage.getItem('token');
    
    const authToken = token || Cookies.get('token'|| newToken);
    
    if (!authToken) {
      console.error('❌ No hay token disponible para Socket');
      console.log('🔍 Cookies disponibles:', document.cookie);
      return null;
    }

    console.log('🔌 Conectando socket a:', this.SOCKET_URL);
    console.log('🔑 Token encontrado:', authToken.substring(0, 20) + '...');

    this.socket = io(this.SOCKET_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: this.maxReconnectAttempts,
      timeout: 10000,
      // ⚡ ENVIAR TOKEN EN AUTH
      auth: {
        token: authToken
      }
    });

    this.setupEventListeners();
    return this.socket;
  }

  setupEventListeners() {
    // Conexión exitosa
    this.socket.on('connect', () => {
      console.log('✅ Socket conectado:', this.socket.id);
      this.isConnected = true;
      this.reconnectAttempts = 0;
    });

    // Desconexión
    this.socket.on('disconnect', (reason) => {
      console.log('🔌 Socket desconectado:', reason);
      this.isConnected = false;
      
      if (reason === 'io server disconnect') {
        console.log('🔄 Reconectando...');
        setTimeout(() => {
          const token = Cookies.get('token');
          this.connect(token);
        }, 1000);
      }
    });

    // Error de conexión
    this.socket.on('connect_error', (error) => {
      console.error('❌ Error de conexión Socket:', error.message);
      this.isConnected = false;
      this.reconnectAttempts++;

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('❌ Máximo de intentos de reconexión alcanzado');
        this.disconnect();
      }
    });

    // Ubicación guardada
    this.socket.on('location:saved', (data) => {
      console.log('✅ Ubicación guardada:', data);
    });

    // Error de ubicación
    this.socket.on('location:error', (data) => {
      console.error('❌ Error de ubicación:', data.message);
    });

    // Usuarios online
    this.socket.on('users:online', (data) => {
      console.log('👥 Usuarios online:', data.count);
    });
  }

  disconnect() {
    this.stopLocationTracking();
    
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      console.log('🔌 Socket desconectado completamente');
    }
  }

  // ==================== LOCATION TRACKING ====================

  startLocationTracking(options = {}) {
    const {
      updateInterval = 10000,
      enableHighAccuracy = true,
      timeout = 5000,
      maximumAge = 0
    } = options;

    if (!this.socket || !this.isConnected) {
      console.error('❌ Socket no conectado. Llama a connect() primero.');
      return false;
    }

    if (!navigator.geolocation) {
      console.error('❌ Geolocalización no soportada en este navegador');
      alert('Tu navegador no soporta geolocalización');
      return false;
    }

    if (this.locationWatchId) {
      console.log('⚠️ Ya está trackeando ubicación');
      return true;
    }

    let lastUpdate = 0;
    this.getCurrentLocation();

    this.locationWatchId = navigator.geolocation.watchPosition(
      (position) => {
        const now = Date.now();
        
        if (now - lastUpdate < updateInterval) {
          return;
        }
        
        lastUpdate = now;

        const locationData = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          speed: position.coords.speed || 0,
          heading: position.coords.heading || 0,
          timestamp: new Date(position.timestamp).toISOString()
        };

        console.log('📍 Enviando ubicación:', {
          lat: locationData.latitude.toFixed(6),
          lng: locationData.longitude.toFixed(6),
          accuracy: locationData.accuracy?.toFixed(0) + 'm'
        });

        this.socket.emit('location:update', locationData);
      },
      (error) => {
        console.error('❌ Error obteniendo ubicación:', error.message);
        this.handleLocationError(error);
      },
      {
        enableHighAccuracy,
        timeout,
        maximumAge
      }
    );

    console.log('✅ Tracking de ubicación iniciado (actualización cada ' + (updateInterval / 1000) + 's)');
    return true;
  }

  getCurrentLocation() {
    if (!navigator.geolocation) {
      console.error('❌ Geolocalización no soportada');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const locationData = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          speed: position.coords.speed || 0,
          heading: position.coords.heading || 0,
          timestamp: new Date(position.timestamp).toISOString()
        };

        console.log('📍 Ubicación actual obtenida');
        
        if (this.socket && this.isConnected) {
          this.socket.emit('location:update', locationData);
        }
      },
      (error) => {
        console.error('❌ Error obteniendo ubicación:', error.message);
        this.handleLocationError(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      }
    );
  }

  stopLocationTracking() {
    if (this.locationWatchId) {
      navigator.geolocation.clearWatch(this.locationWatchId);
      this.locationWatchId = null;
      console.log('🛑 Tracking de ubicación detenido');
      return true;
    }
    return false;
  }

  handleLocationError(error) {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        console.error('❌ Permiso de ubicación denegado');
        alert('Por favor, permite el acceso a tu ubicación en la configuración del navegador');
        break;
      case error.POSITION_UNAVAILABLE:
        console.error('❌ Ubicación no disponible');
        break;
      case error.TIMEOUT:
        console.error('❌ Timeout al obtener ubicación');
        break;
      default:
        console.error('❌ Error desconocido:', error.message);
        break;
    }
  }

  // ==================== EVENTOS DE UBICACIÓN ====================

  onLocationUpdate(callback) {
    if (!this.socket) {
      console.error('❌ Socket no conectado');
      return;
    }
    this.socket.on('location:updated', callback);
  }

  offLocationUpdate(callback) {
    if (!this.socket) return;
    this.socket.off('location:updated', callback);
  }

  getAllLocations(callback) {
    if (!this.socket || !this.isConnected) {
      console.error('❌ Socket no conectado');
      return;
    }

    this.socket.emit('locations:getAll');
    this.socket.once('locations:list', callback);
  }

  requestUserLocation(userId, callback) {
    if (!this.socket || !this.isConnected) {
      console.error('❌ Socket no conectado');
      return;
    }

    this.socket.emit('location:request', userId);
    this.socket.once('location:response', callback);
  }

  // ==================== EVENTOS DE USUARIOS ====================

  onUsersOnline(callback) {
    if (!this.socket) {
      console.error('❌ Socket no conectado');
      return;
    }
    this.socket.on('users:online', callback);
  }

  offUsersOnline(callback) {
    if (!this.socket) return;
    this.socket.off('users:online', callback);
  }

  // ==================== UTILIDADES ====================

  isSocketConnected() {
    return this.isConnected && this.socket?.connected;
  }

  isTrackingLocation() {
    return this.locationWatchId !== null;
  }

  getSocket() {
    if (!this.socket) {
      throw new Error('Socket no conectado. Llama a connect() primero.');
    }
    return this.socket;
  }

  emit(event, data) {
    if (!this.socket || !this.isConnected) {
      console.error('❌ Socket no conectado');
      return false;
    }
    this.socket.emit(event, data);
    return true;
  }

  on(event, callback) {
    if (!this.socket) {
      console.error('❌ Socket no conectado');
      return;
    }
    this.socket.on(event, callback);
  }

  off(event, callback) {
    if (!this.socket) return;
    this.socket.off(event, callback);
  }
}

const socketService = new SocketService();
export default socketService;